import { DateTime } from 'luxon';
import { fromISO } from 'utils/luxon-helpers';

interface CalculateWorkDays {
  from: string;
  to: string;
  inclusive?: boolean;
  workDays?: Array<number>;
}

export function calculateWorkDaysBetweenDates({
  from,
  to,
  inclusive,
  workDays,
}: CalculateWorkDays): number {
  const totalDiff = fromISO(to).diff(fromISO(from), 'days').days;
  let newDiff = 0;
  for (let i = 0; i <= totalDiff; i++) {
    const dtFrom = fromISO(from).plus({ days: i });
    if (isWorkDay(dtFrom.toISODate(), workDays)) {
      newDiff++;
    }
  }
  if (!inclusive) {
    return newDiff - 1;
  }

  return newDiff;
}

export function addWorkDaysToDate(date: string, days: number, workDays?: Array<number>): string {
  // partially lifted from https://github.com/amaidah/luxon-business-days/blob/master/src/index.js
  const isNegative = days < 0;

  let businessDaysLeftToAdd = isNegative ? Math.round(-1 * days) : Math.round(days);
  let dt = fromISO(date);
  while (businessDaysLeftToAdd > 0) {
    const oneDayByDirection = isNegative ? -1 : 1;

    dt = dt.plus({ days: oneDayByDirection });

    if (isWorkDay(dt.toISODate(), workDays)) {
      businessDaysLeftToAdd--;
    }
  }

  return dt.toISODate();
}

export function subtractWorkDaysFromDate(
  date: string,
  days: number,
  workDays?: Array<number>
): string {
  return addWorkDaysToDate(date, -days, workDays);
}

const DEFAULT_WORKDAYS = [1, 2, 3, 4, 5];
export function isWorkDay(date: string | Date, workDays?: Array<number>): boolean {
  const days = workDays ?? DEFAULT_WORKDAYS;
  const parsedDate = date instanceof Date ? DateTime.fromJSDate(date) : fromISO(date);
  return days.includes(parsedDate.weekday);
}

export type Weekdays = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export const weekdayToFullDayMap: Record<Weekdays, string> = Object.freeze({
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
});

export const weekdayToNumberMap: Record<Weekdays, number> = Object.freeze({
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
});

export const weekdayToISONumberMap: Record<Weekdays, number> = Object.freeze({
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  SUN: 7,
});

export const ISO_WEEKDAYS = Object.keys(weekdayToISONumberMap);

export const DEFAULT_WEEKDAYS = 'MON,TUE,WED,THU,FRI';
export const DEFAULT_WEEKDAYS_LIST = DEFAULT_WEEKDAYS.split(',');

export function weekdayStringToISONumber(weekdays: string): Array<number> {
  const getDays = weekdays.split(',') as Array<Weekdays>;
  return getDays.map(day => weekdayToISONumberMap[day]);
}
